import { disciplines } from "@prisma-local/client";
import {
  CompetitionFormat,
  disciplineFormatMap,
  Exercise,
  exercisesByFormat,
} from "../src/types.js";
import dico from "./language/dico.js";
import { AthletesModel } from "./types.js";

/**
 * Détermine le format de compétition basé sur le code de discipline
 *
 * @param discipline - La discipline (objet ou avec propriété code)
 * @returns Le format de compétition correspondant
 *
 * @example
 * const format = getCompetitionFormat({ code: 'FA' }); // CompetitionFormat.FULL_POWER
 * const format = getCompetitionFormat({ code: 'DC' }); // CompetitionFormat.SINGLE_LIFT
 */
export const getCompetitionFormat = (
  discipline: disciplines | { code: string },
): CompetitionFormat => {
  return disciplineFormatMap[discipline.code] || CompetitionFormat.FULL_POWER;
};

/**
 * Détermine le format de compétition basé sur l'ID de discipline
 * Nécessite une fonction pour récupérer la discipline depuis la base de données
 *
 * @param disciplineId - L'ID de la discipline
 * @param getDisciplineById - Fonction pour récupérer la discipline par ID
 * @returns Le format de compétition correspondant
 *
 * @example
 * const format = await getCompetitionFormatById(1, (id) => prisma.disciplines.findUnique({ where: { id } }));
 */
export const getCompetitionFormatById = (
  disciplineId: number,
): CompetitionFormat => {
  switch (disciplineId) {
    case 1:
      return CompetitionFormat.FULL_POWER;
    case 6:
    case 7:
      return CompetitionFormat.SINGLE_LIFT;
    default:
      return CompetitionFormat.FULL_POWER;
  }
};

/**
 * Détermine le format de compétition basé sur un athlète avec sa discipline incluse
 *
 * @param athlete - L'athlète avec sa discipline incluse
 * @returns Le format de compétition correspondant
 *
 * @example
 * const format = getCompetitionFormatFromAthlete(athleteWithDiscipline);
 */
export const getCompetitionFormatFromAthlete = (
  athlete: AthletesModel | null | undefined,
): CompetitionFormat => {
  return getCompetitionFormatById(athlete?.disciplineId || 1);
};

/**
 * Obtient les exercices actifs pour un format de compétition donné
 *
 * @param format - Le format de compétition
 * @returns Liste des exercices actifs pour ce format
 *
 * @example
 * const exercises = getActiveExercises(CompetitionFormat.FULL_POWER);
 * // Retourne: [{ name: "squat", lifts: [...] }, { name: "bench", lifts: [...] }, { name: "deadlift", lifts: [...] }]
 *
 * const exercises = getActiveExercises(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: [{ name: "bench", lifts: [...] }]
 */
export const getActiveExercises = (format: CompetitionFormat): Exercise[] => {
  return exercisesByFormat[format] ?? [];
};

/**
 * Lift kinds (squat / bench / deadlift) that are active for a competition format.
 * SINGLE_LIFT (DC/BP) only returns bench.
 *
 * @param format - The competition format.
 * @returns Active lift kind names in display order.
 */
export const getActiveLiftKinds = (
  format: CompetitionFormat,
): Exercise["name"][] => {
  return getActiveExercises(format).map((exercise) => exercise.name);
};

/**
 * True when the event is bench-only (DC / BP).
 *
 * @param format - The competition format.
 * @returns Whether squat and deadlift must be hidden.
 */
export const isSingleLiftFormat = (format: CompetitionFormat): boolean => {
  return format === CompetitionFormat.SINGLE_LIFT;
};

/**
 * Resolves the competition format for speaker / ranking UI.
 * Athlete discipline wins (DC/BP → SINGLE_LIFT) so web grids hide FA columns
 * even when group.competitionFormat was left at FULL_POWER by mistake.
 * Group format is only a fallback when the athlete has no discipline info.
 *
 * @param athlete - Athlete with optional discipline relation.
 * @param groupFormat - Optional group.competitionFormat string.
 * @returns FULL_POWER or SINGLE_LIFT.
 */
export const resolveAthleteCompetitionFormat = (
  athlete: AthletesModel | null | undefined,
  groupFormat?: string | CompetitionFormat | null,
): CompetitionFormat => {
  // Discipline code is the source of truth for which lifts the athlete competes in
  if (athlete?.discipline?.code) {
    return getCompetitionFormat(athlete.discipline);
  }
  // Fall back to disciplineId when the relation was stripped from the payload
  if (athlete?.disciplineId != null && athlete.disciplineId > 0) {
    return getCompetitionFormatFromAthlete(athlete);
  }
  if (
    groupFormat === CompetitionFormat.SINGLE_LIFT ||
    groupFormat === CompetitionFormat.FULL_POWER
  ) {
    return groupFormat;
  }
  return CompetitionFormat.FULL_POWER;
};

/**
 * Resolves the competition format for a whole ranking table / group UI.
 * SINGLE_LIFT when the group stores it, or when every athlete with a known
 * discipline is DC/BP (covers live web when format was defaulted to FULL_POWER).
 *
 * @param group - Group with optional competitionFormat and athletes.
 * @returns FULL_POWER or SINGLE_LIFT.
 */
export const resolveGroupCompetitionFormat = (
  group:
    | {
        competitionFormat?: string | CompetitionFormat | null;
        athletes?: AthletesModel[] | null;
      }
    | null
    | undefined,
): CompetitionFormat => {
  if (group?.competitionFormat === CompetitionFormat.SINGLE_LIFT) {
    return CompetitionFormat.SINGLE_LIFT;
  }
  const athletes = group?.athletes ?? [];
  // Infer from athletes when the group flag is missing or wrongly FULL_POWER
  const knownFormats: CompetitionFormat[] = [];
  for (const athlete of athletes) {
    const hasDiscipline =
      Boolean(athlete.discipline?.code) || (athlete.disciplineId ?? 0) > 0;
    if (!hasDiscipline) {
      continue;
    }
    knownFormats.push(resolveAthleteCompetitionFormat(athlete, null));
  }
  if (
    knownFormats.length > 0 &&
    knownFormats.every((format) => format === CompetitionFormat.SINGLE_LIFT)
  ) {
    return CompetitionFormat.SINGLE_LIFT;
  }
  if (group?.competitionFormat === CompetitionFormat.FULL_POWER) {
    return CompetitionFormat.FULL_POWER;
  }
  return CompetitionFormat.FULL_POWER;
};

/**
 * Obtient tous les champs de mouvement pertinents pour un format
 *
 * @param format - Le format de compétition
 * @returns Liste des champs de mouvement pertinents
 *
 * @example
 * const fields = getRelevantLiftFields(CompetitionFormat.FULL_POWER);
 * // Retourne: ["squat1", "squat2", "squat3", "bench1", "bench2", "bench3", "deadlift1", "deadlift2", "deadlift3"]
 *
 * const fields = getRelevantLiftFields(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: ["bench1", "bench2", "bench3"]
 */
export const getRelevantLiftFields = (format: CompetitionFormat): string[] => {
  return getActiveExercises(format).flatMap((ex: Exercise) => ex.lifts);
};

/**
 * Vérifie si un champ de mouvement est pertinent pour un format
 *
 * @param format - Le format de compétition
 * @param liftField - Le champ de mouvement à vérifier
 * @returns true si le champ est pertinent pour ce format
 *
 * @example
 * const isRelevant = isLiftRelevant(CompetitionFormat.SINGLE_LIFT, "bench1"); // true
 * const isRelevant = isLiftRelevant(CompetitionFormat.SINGLE_LIFT, "squat1"); // false
 */
export const isLiftRelevant = (
  format: CompetitionFormat,
  liftField: string,
): boolean => {
  const relevantFields = getRelevantLiftFields(format);
  return relevantFields.includes(liftField);
};

/**
 * Détermine le format d'un groupe
 *
 * @param group - Le groupe avec sa propriété competitionFormat
 * @returns Le format de compétition du groupe
 *
 * @example
 * const format = getGroupFormat({ competitionFormat: "FULL_POWER" }); // CompetitionFormat.FULL_POWER
 * const format = getGroupFormat({ competitionFormat: "SINGLE_LIFT" }); // CompetitionFormat.SINGLE_LIFT
 */
export const getGroupFormat = (group: {
  competitionFormat: string;
}): CompetitionFormat => {
  return group.competitionFormat as CompetitionFormat;
};

/**
 * Vérifie si une discipline est compatible avec un format de compétition
 *
 * @param discipline - La discipline à vérifier
 * @param format - Le format de compétition
 * @returns true si la discipline est compatible avec le format
 *
 * @example
 * const compatible = isDisciplineCompatible({ code: 'FA' }, CompetitionFormat.FULL_POWER); // true
 * const compatible = isDisciplineCompatible({ code: 'DC' }, CompetitionFormat.SINGLE_LIFT); // true
 * const compatible = isDisciplineCompatible({ code: 'FA' }, CompetitionFormat.SINGLE_LIFT); // false
 */
export const isDisciplineCompatible = (
  discipline: disciplines | { code: string },
  format: CompetitionFormat,
): boolean => {
  const disciplineFormat = getCompetitionFormat(discipline);
  return disciplineFormat === format;
};

/**
 * Filtre les disciplines pour ne garder que celles compatibles avec un format
 *
 * @param disciplines - Liste des disciplines à filtrer
 * @param format - Le format de compétition cible
 * @returns Liste des disciplines compatibles avec le format
 *
 * @example
 * const compatibleDisciplines = filterCompatibleDisciplines(allDisciplines, CompetitionFormat.SINGLE_LIFT);
 * // Retourne seulement les disciplines DC et BP
 */
export const filterCompatibleDisciplines = (
  disciplines: (disciplines | { code: string })[],
  format: CompetitionFormat,
): (disciplines | { code: string })[] => {
  return disciplines.filter((discipline) =>
    isDisciplineCompatible(discipline, format),
  );
};

/**
 * Obtient les champs de statut pertinents pour un format
 *
 * @param format - Le format de compétition
 * @returns Liste des champs de statut pertinents
 *
 * @example
 * const statusFields = getRelevantStatusFields(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: ["bench1Status", "bench2Status", "bench3Status"]
 */
export const getRelevantStatusFields = (
  format: CompetitionFormat,
): string[] => {
  const liftFields = getRelevantLiftFields(format);
  return liftFields.map((field) => `${field}Status`);
};

/**
 * Obtient les champs de hauteur pertinents pour un format
 *
 * @param format - Le format de compétition
 * @returns Liste des champs de hauteur pertinents
 *
 * @example
 * const heightFields = getRelevantHeightFields(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: ["hauteurDC", "hauteurSecu"]
 */
export const getRelevantHeightFields = (
  format: CompetitionFormat,
): string[] => {
  if (format === CompetitionFormat.SINGLE_LIFT) {
    return ["hauteurDc", "hauteurSecu"];
  }
  return ["hauteurSquat", "hauteurDc", "hauteurSecu"];
};

/**
 * Obtient les champs techniques pertinents pour un format
 *
 * @param format - Le format de compétition
 * @returns Liste des champs techniques pertinents
 *
 * @example
 * const techFields = getRelevantTechnicalFields(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: ["cales", "positionRack"]
 */
export const getRelevantTechnicalFields = (): string[] => {
  // Les champs techniques sont identiques pour tous les formats
  return ["cales", "positionRack"];
};

/**
 * Obtient tous les champs pertinents pour un format (mouvements + hauteurs + techniques)
 *
 * @param format - Le format de compétition
 * @returns Liste de tous les champs pertinents
 *
 * @example
 * const allFields = getAllRelevantFields(CompetitionFormat.SINGLE_LIFT);
 * // Retourne: ["bench1", "bench2", "bench3", "hauteurDC", "hauteurSecu", "cales", "positionRack"]
 */
export const getAllRelevantFields = (format: CompetitionFormat): string[] => {
  return [
    ...getRelevantLiftFields(format),
    ...getRelevantHeightFields(format),
    ...getRelevantTechnicalFields(),
  ];
};

/**
 * Valide qu'un athlète est compatible avec le format de son groupe
 *
 * @param athlete - L'athlète à valider
 * @param group - Le groupe de l'athlète
 * @returns true si l'athlète est compatible avec le format du groupe
 *
 * @example
 * const isValid = validateAthleteGroupCompatibility(athlete, group);
 * // Vérifie que la discipline de l'athlète correspond au format du groupe
 */
export const validateAthleteGroupCompatibility = (
  athlete: AthletesModel,
  group: { competitionFormat: string },
): boolean => {
  if (!athlete.discipline) return false;

  const athleteFormat = getCompetitionFormat(athlete.discipline);
  const groupFormat = getGroupFormat(group);

  return athleteFormat === groupFormat;
};

/**
 * Obtient le nom d'affichage d'un format de compétition
 *
 * @param format - Le format de compétition
 * @returns Le nom d'affichage du format
 *
 * @example
 * const name = getFormatDisplayName(CompetitionFormat.FULL_POWER); // "3 Mouvements (FA, PL)"
 * const name = getFormatDisplayName(CompetitionFormat.SINGLE_LIFT); // "1 Mouvement (DC, BP)"
 */
export const getFormatDisplayName = (format: CompetitionFormat): string => {
  switch (format) {
    case CompetitionFormat.FULL_POWER:
      return "3 Mouvements (FA, PL)";
    case CompetitionFormat.SINGLE_LIFT:
      return "1 Mouvement (DC, BP)";
    default:
      return "Format inconnu";
  }
};

/**
 * Obtient la description d'un format de compétition
 *
 * @param format - Le format de compétition
 * @returns La description du format
 *
 * @example
 * const desc = getFormatDescription(CompetitionFormat.FULL_POWER);
 * // "Compétition avec squat, développé couché et soulevé de terre"
 */
export const getFormatDescription = (format: CompetitionFormat): string => {
  switch (format) {
    case CompetitionFormat.FULL_POWER:
      return dico().export.exportFullPowerAthletes;
    case CompetitionFormat.SINGLE_LIFT:
      return dico().export.exportSingleLiftAthletes;
  }
};
/**
 * Obtient le nom d'affichage d'un format de compétition
 *
 * @param format - Le format de compétition
 * @returns Le nom d'affichage du format
 *
 * @example
 * const name = getFormatName(CompetitionFormat.FULL_POWER); // "FA/PL"
 * const name = getFormatName(CompetitionFormat.SINGLE_LIFT); // "DC/BP"
 */
export const getFormatName = (format: CompetitionFormat): string => {
  return format === CompetitionFormat.FULL_POWER
    ? dico().meet.formats.fullPower
    : dico().meet.formats.singleLift;
};

/**
 * Returns opener fields (first attempt of each exercise) for a competition format.
 *
 * @param format - The competition format.
 * @returns Opener field names (e.g. squat1, bench1, deadlift1).
 * @example
 * getRelevantOpenerFields(CompetitionFormat.SINGLE_LIFT); // ["bench1"]
 */
export const getRelevantOpenerFields = (
  format: CompetitionFormat,
): string[] => {
  const openers: string[] = [];
  for (const exercise of getActiveExercises(format)) {
    const opener = exercise.lifts[0];
    if (opener) openers.push(opener);
  }
  return openers;
};

/**
 * Minimal athlete shape required to validate group-start completeness.
 * Numeric fields accept Prisma Decimal / number / string via Number() coercion.
 * `isDisqualified` is accepted for typing but does not exempt required fields.
 */
export type AthleteStartCompletenessFields = {
  pdc?: unknown;
  sexe?: unknown;
  dateNaissance?: unknown;
  lot?: unknown;
  squat1?: unknown;
  bench1?: unknown;
  deadlift1?: unknown;
  hauteurSquat?: unknown;
  hauteurDc?: unknown;
  hauteurSecu?: unknown;
  isDisqualified?: boolean | null;
};

/**
 * Returns true when a numeric athlete field is missing or non-positive.
 *
 * @param value - Raw field value from the athlete row.
 * @returns Whether the value is considered missing for start validation.
 */
const isMissingPositiveNumber = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return true;
  const numeric = Number(value);
  return Number.isNaN(numeric) || numeric <= 0;
};

/**
 * Returns true when a text field is null/undefined/blank.
 *
 * @param value - Raw field value from the athlete row.
 * @returns Whether the value is considered missing.
 */
const isMissingText = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  return String(value).trim() === "";
};

/**
 * Returns true when a date field is missing or not parseable.
 *
 * @param value - Raw birth date (Date, ISO string, etc.).
 * @returns Whether the value is considered missing.
 */
const isMissingDate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return true;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime());
};

/**
 * Returns true when the athlete is missing required identity or lift setup data.
 * Required: bodyweight, sex, birth date, lot, openers and heights for the format.
 * Hors Match (`isDisqualified`) does not skip these checks - HM athletes still need
 * rack heights and openers for loaders / bar order.

 *
 * @param athlete - Athlete row (or subset of fields).
 * @param format - Competition format of the group.
 * @returns Whether the athlete is incomplete for starting the group.
 * @example
 * isAthleteIncompleteForStart({ pdc: 0, sexe: "M", dateNaissance: "2000-01-01", lot: 1, bench1: 100, hauteurDc: 10, hauteurSecu: 5 }, CompetitionFormat.SINGLE_LIFT);
 * // true - bodyweight missing
 */
export const isAthleteIncompleteForStart = (
  athlete: AthleteStartCompletenessFields,
  format: CompetitionFormat,
): boolean => {
  if (isMissingPositiveNumber(athlete.pdc)) return true;
  if (isMissingText(athlete.sexe)) return true;
  if (isMissingDate(athlete.dateNaissance)) return true;
  if (isMissingPositiveNumber(athlete.lot)) return true;
  const athleteRecord = athlete as Record<string, unknown>;
  for (const field of getRelevantOpenerFields(format)) {
    if (isMissingPositiveNumber(athleteRecord[field])) return true;
  }
  for (const field of getRelevantHeightFields(format)) {
    if (isMissingPositiveNumber(athleteRecord[field])) return true;
  }
  return false;
};

/**
 * Returns true when every relevant opener for the format is filled (> 0).
 * Disqualified athletes are treated as done (they do not compete).
 *
 * @param athlete - Athlete row (or subset of fields).
 * @param format - Competition format of the group.
 * @returns Whether openers are entered for this athlete.
 */
export const hasAthleteOpenersEntered = (
  athlete: AthleteStartCompletenessFields,
  format: CompetitionFormat,
): boolean => {
  if (athlete.isDisqualified) return true;
  const athleteRecord = athlete as Record<string, unknown>;
  for (const field of getRelevantOpenerFields(format)) {
    if (isMissingPositiveNumber(athleteRecord[field])) return false;
  }
  return true;
};

/**
 * Returns true when every competing athlete has all openers entered.
 *
 * @param athletes - Athletes belonging to the group.
 * @param format - Competition format of the group.
 * @returns Whether all openers are entered (empty list → false).
 */
export const areAllGroupOpenersEntered = <T>(
  athletes: readonly T[],
  format: CompetitionFormat,
): boolean => {
  if (athletes.length === 0) return false;
  return athletes.every((athlete) =>
    hasAthleteOpenersEntered(athlete as AthleteStartCompletenessFields, format),
  );
};

/**
 * Filters athletes that block starting a group due to incomplete data.
 * Disqualified (HM) athletes never block group start.
 *
 * @param athletes - Athletes belonging to the group.
 * @param format - Competition format of the group.
 * @returns Athletes missing required fields (pdc, sexe, dateNaissance, lot, openers, heights).
 */
export const getIncompleteAthletesForStart = <T>(
  athletes: readonly T[],
  format: CompetitionFormat,
): T[] => {
  return athletes.filter((athlete) => {
    const fields = athlete as AthleteStartCompletenessFields;
    // HM athletes do not block start, but still show as incomplete in the grid
    if (fields.isDisqualified) return false;
    return isAthleteIncompleteForStart(fields, format);
  });
};
